import React, { ReactElement } from 'react'

import styled from '@emotion/styled'

import { useHelpTextId } from '../../../hooks/useHelpTextId'
import { ThemeSpacing } from '../../../theme/spacing'
import { Flex } from '../../layout/Flex'
import BaseCell, { BaseCellProps } from '../BaseCell/BaseCell'
import ArcadeCheckbox, { ArcadeCheckboxProps } from '../CheckboxInput/ArcadeCheckbox'
import { cellHoverStyles } from '../InteractiveCell/InteractiveCell'

/**
 * We omit 'value' because the underlying checkbox input element shared with Mooncake CheckboxInput defines its own 'value' prop,
 * which does not match the type from the native input HTML element.
 *
 * When we use this Arcade component, we will not be passing a 'value' prop because most of these will be wrapped in a react-hook-form,
 * which does not use that prop.
 *
 * We will further decouple as we migrate off of the Mooncake component.
 */

const StyledLabel = styled.label<{ disabled: boolean }>`
  cursor: ${({ disabled }) => (disabled ? 'not-allowed' : 'pointer')};
  display: block;
  margin: -${props => props.theme.spacing.m} -${props => props.theme.spacing.l};
  padding: ${props => props.theme.spacing.m} ${props => props.theme.spacing.l};
  position: relative;

  ${({ theme, disabled }) => cellHoverStyles(theme, disabled)}

  /* focused */
  ${({ theme }) => theme.selectors.keyboard} &:has(input:focus) {
    border-radius: 0.25rem;
    outline: 0.125rem solid ${({ theme }) => theme.arcadeColors.component.inputBorderFocus};
  }
`
const Accessory = styled(Flex)<{ disabled: boolean }>`
  opacity: ${({ disabled }) => (disabled ? 0.4 : undefined)};
`
export interface CheckboxCellProps extends Omit<ArcadeCheckboxProps, 'value'>, Pick<BaseCellProps, 'label'> {
  cellProps?: Omit<BaseCellProps, 'accessory' | 'disabled' | 'label'>
  labelProps?: React.LabelHTMLAttributes<HTMLLabelElement>
  headerSpacing?: ThemeSpacing[keyof ThemeSpacing]
  onChange?: (event: React.ChangeEvent<HTMLInputElement>) => void
  accessoryLabel?: ReactElement
}

const CheckboxCell = React.forwardRef<HTMLDivElement, CheckboxCellProps>((props, ref) => {
  const {
    label,
    disabled = false,
    cellProps,
    labelProps,
    headerSpacing,
    onChange,
    accessoryLabel,
    ...restProps
  } = props

  const helpTextId = useHelpTextId(cellProps)

  return (
    <StyledLabel disabled={!!disabled} {...labelProps} data-testid="checkbox-cell-label">
      <BaseCell
        ref={ref}
        label={label}
        accessory={
          <Accessory flexDirection="row" gap="s" disabled={disabled}>
            {accessoryLabel}
            <ArcadeCheckbox disabled={disabled} {...restProps} onChange={onChange} helpTextId={helpTextId} />
          </Accessory>
        }
        disabled={disabled}
        headerSpacing={headerSpacing}
        {...cellProps}
        helpTextId={helpTextId}
      />
    </StyledLabel>
  )
})

export default CheckboxCell
